import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import ImportDiffDetails from './ImportDiffDetails';
import ImportFailureNotice from './shared/ImportFailureNotice';
import { notConsideredRows, restoredLeadingZerosNote } from './watchlist_table/SkippedUploadRows';
import {
    HistoryCategoryChip,
    HistoryMetric,
    HistoryStatusIcon,
    HistorySummaryMetrics,
} from './shared/HistoryLedgerPrimitives';
import { watchlistPanelSx } from '../styles/watchlistMsiStyles';
import { securitySubmittedIdentifier } from '../utils/securityIdentityPresentation';
import { normalizeWatchlistLifecycle } from '../utils/watchlistLifecycle';
import { isNothingMatched, nothingMatchedMessage } from '../utils/watchlistImportOutcome';
import SecurityIdentityFieldSelector, {
    SecurityIdentityColumnHeader,
} from './commonComponents/SecurityIdentityFieldSelector';

const WATCHLIST_IMPACT_IDENTITY_SELECTOR_ID = 'watchlist-impact-identity-field';

const sourceConfig = {
    manual_upload: { label: 'Upload', color: 'secondary', tooltip: 'Portfolio file upload' },
    ibkr: { label: 'IBKR', color: 'info', tooltip: 'IBKR portfolio sync' },
    ibkr_intraday: { label: 'IBKR Intraday', color: 'info', tooltip: 'IBKR intraday portfolio sync' },
    file_drop: { label: 'File Drop', color: 'primary', tooltip: 'Portfolio file drop' },
    restore: { label: 'Restore', color: 'success', tooltip: 'Restored from portfolio history' },
    watchlist_sync: { label: 'Watchlist Sync', color: 'warning', tooltip: 'Portfolio update from the Watchlist' },
    watchlist_editor: { label: 'Watchlist Editor', color: 'primary', tooltip: 'Portfolio update from the Watchlist editor' },
    security_resolution: { label: 'Security Resolution', color: 'secondary', tooltip: 'Watchlist security-resolution update' },
    upload: { label: 'Upload', color: 'primary', tooltip: 'Watchlist file upload' },
};

function pendingSourcePresentation(importRun, metadata) {
    if (metadata?.combined_draft_contract) return { label: 'Combined change', color: 'primary', tooltip: 'Your pending Watchlist edits' };
    if (metadata?.action === 'restore') return { label: 'Restore', color: 'primary', tooltip: 'Restore a Watchlist version' };
    if (metadata?.action === 'optimizer_actual_restoration') {
        return { label: 'Saved Actual holdings', color: 'success',
            tooltip: 'Missing Actual shares recovered from the saved optimization' };
    }
    if (metadata?.action === 'watchlist_context_refresh') {
        return {
            label: 'Context change',
            color: 'warning',
            tooltip: 'Recheck the saved Watchlist under the available context',
        };
    }
    if (
        importRun?.source === 'upload'
        && metadata?.ingress_kind === 'm365_excel'
    ) {
        return {
            label: 'Microsoft 365 Excel',
            color: 'primary',
            tooltip: 'Watchlist changes staged from the linked Excel worksheet',
        };
    }
    return sourceConfig[importRun?.source] || {
        label: importRun?.source || 'Unknown',
        color: 'default',
        tooltip: '',
    };
}

// Mirrors _NON_REPLACEMENT_SOURCES in functions/services/pos_sizing_actual_change_policy.py.
const NON_RESETTING_SOURCES = new Set(['watchlist_editor', 'watchlist_sync']);

function finiteNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

// Skipped-row codes the review explains, as "N <rows> skipped".
const SKIPPED_ROW_REASONS = [
    {
        rows: ['row with a blank identifier', 'rows with a blank identifier'],
        codes: ['missing_identifier_value', 'portfolio_upload_identifier_missing'],
    },
    {
        rows: ['row with a malformed identifier', 'rows with a malformed identifier'],
        codes: ['invalid_identifier_length', 'invalid_identifier_value'],
    },
    {
        rows: ['row with an unrecognised country', 'rows with an unrecognised country'],
        codes: ['unknown_country_value'],
    },
    { rows: ['row with an invalid MIC', 'rows with an invalid MIC'], codes: ['invalid_mic_value'] },
    { rows: ['duplicate row', 'duplicate rows'], codes: ['duplicate_identifier_row'] },
];

function importCleanupSummary(inputValidation, { rowsListed = false } = {}) {
    const validation = inputValidation && typeof inputValidation === 'object'
        ? inputValidation
        : {};
    const errors = Array.isArray(validation.errors) ? validation.errors : [];
    const aggregateCodes = validation.error_code_counts
        && typeof validation.error_code_counts === 'object'
        ? validation.error_code_counts
        : {};
    const blankRows = finiteNumber(validation.blank_row_count_removed);
    const blankColumns = finiteNumber(validation.blank_column_count_removed);
    const parts = [];
    let skippedRows = 0;
    SKIPPED_ROW_REASONS.forEach(({ rows, codes }) => {
        // The error list is a capped preview; the code counts cover every row.
        const count = Math.max(
            errors.filter((error) => codes.includes(String(error?.code || ''))).length,
            codes.reduce((total, code) => total + finiteNumber(aggregateCodes[code]), 0),
        );
        if (count > 0) {
            skippedRows += count;
            parts.push(`${count} ${rows[count === 1 ? 0 : 1]} skipped`);
        }
    });
    if (blankRows > 0) {
        parts.push(`${blankRows} fully blank row${blankRows === 1 ? '' : 's'} ignored`);
    }
    if (blankColumns > 0) {
        parts.push(`${blankColumns} fully blank unnamed column${blankColumns === 1 ? '' : 's'} ignored`);
    }
    if (!parts.length) return '';
    const layoutCellsIgnored = blankRows > 0 || blankColumns > 0;
    // A Watchlist review lists its skipped rows, with how to fix them.
    const fixSkippedRows = skippedRows > 0 && !rowsListed;
    let assurance = '';
    if (fixSkippedRows && layoutCellsIgnored) {
        assurance = ' Fix the skipped rows in the file and upload it again; blank layout cells were ignored.';
    } else if (fixSkippedRows) {
        assurance = ' Fix the skipped rows in the file and upload it again.';
    } else if (layoutCellsIgnored) {
        assurance = ' Blank layout cells were ignored.';
    }
    return `Import cleanup: ${parts.join('; ')}.${assurance}`;
}

export default function PendingImportCard({
    importRun,
    onApply,
    onIgnore,
    applying = false,
    showActions = true,
    title,
    currentPortfolioTimestamp,
    headerActions,
    summaryOnly = false,
    visibleCountKeys = null,
    visibleDiffSections = null,
    diffDetailsMode = 'portfolio',
    applyDisabled = false,
    applyDisabledReason = '',
    onKeepInWatchlist = null,
}) {
    const [impactIdentityField, setImpactIdentityField] = useState('company_name');
    const [impactIdentityAnchorEl, setImpactIdentityAnchorEl] = useState(null);
    if (!importRun) return null;

    const diffDetails = importRun.diff_details_json || {};
    const diffSummary = importRun.diff_summary_json || {};
    const counts = diffSummary.counts || {
        added: diffDetails.added?.length || 0,
        removed: diffDetails.removed?.length || 0,
        changed: diffDetails.changed?.length || 0,
        unchanged: diffDetails.unchanged_count || 0,
    };
    const ignoredWatchlist = diffSummary.ignored_watchlist || [];
    const expansionWarnings = diffSummary.warnings?.watchlist_identifier_expansions || [];
    const metadata = importRun.source_metadata_json || {};
    const inputValidation = diffSummary.input_validation || metadata.input_validation;
    const isWatchlistUpload = importRun.source === 'upload';
    // Only a Watchlist upload's review lists the rows; portfolio imports keep
    // their cleanup disclosure aggregate-only.
    const notConsidered = notConsideredRows(
        diffDetailsMode === 'watchlist_upload' ? inputValidation : null,
    );
    const cleanupSummary = [
        importCleanupSummary(inputValidation, {
            rowsListed: notConsidered.rows.length > 0,
        }),
        restoredLeadingZerosNote(inputValidation),
    ].filter(Boolean).join(' ');
    const portfolioImpact = diffSummary.portfolio_impact || {};
    const portfolioImpactRows = Array.isArray(portfolioImpact.securities)
        ? portfolioImpact.securities
        : [];
    const hasPortfolioImpact = isWatchlistUpload && portfolioImpactRows.length > 0;
    let watchlistLifecycle = null;
    if (importRun.source === 'upload') {
        try {
            watchlistLifecycle = normalizeWatchlistLifecycle(importRun.lifecycle);
        } catch (_error) {
            watchlistLifecycle = null;
        }
    }
    const msiReviewPending = watchlistLifecycle?.status === 'pending_review';
    const msiResolutionPending = ['queued', 'running'].includes(watchlistLifecycle?.status);
    const nothingMatched = isNothingMatched(watchlistLifecycle);
    const drasticWarning = (
        importRun.drastic_change_warning
        || metadata.drastic_change_warning
        || diffSummary.drastic_change_warning
        || (diffSummary.drastic_change ? { removed_count: counts.removed || 0 } : null)
    );
    const duplicateOfCurrent = Boolean(importRun.duplicate_of_current || metadata.duplicate_of_current);
    const watchlistStale = importRun.is_watchlist_stale === true;
    const emptyFileReplacement = ['manual_upload', 'file_drop'].includes(importRun.source)
        && diffSummary.watchlist_overlap_status === 'zero_overlap';
    const effectiveApplyDisabled = applyDisabled || watchlistStale || emptyFileReplacement;
    const effectiveApplyDisabledReason = watchlistStale
        ? 'The Watchlist changed after this import was staged. Ignore it and stage a new import.'
        : emptyFileReplacement
            ? 'No imported securities match your Watchlist. Upload again to review adding them before importing positions.'
            : applyDisabledReason;
    const drasticRemovedCount = finiteNumber(drasticWarning?.removed_count, finiteNumber(counts.removed));
    const drasticPriorCount = finiteNumber(
        drasticWarning?.watchlist_size_before,
        finiteNumber(diffSummary.watchlist_size_before),
    );
    const rawDrasticFraction = finiteNumber(drasticWarning?.removed_fraction, NaN);
    const drasticRemovedFraction = Number.isFinite(rawDrasticFraction)
        ? rawDrasticFraction
        : (drasticPriorCount > 0 ? drasticRemovedCount / drasticPriorCount : 0);
    const drasticRemovedPercent = `${Math.round(Math.max(0, drasticRemovedFraction) * 100)}%`;
    const provisionalIdentityDiff = Boolean(msiReviewPending || diffSummary.provisional === true);

    const unchangedWatchlistUpload = isWatchlistUpload && duplicateOfCurrent;
    const canIgnore = Boolean(onIgnore)
        && importRun.source !== 'watchlist_sync'
        && !unchangedWatchlistUpload
        && !hasPortfolioImpact;
    const applyLabel = unchangedWatchlistUpload
        ? 'Close'
        : hasPortfolioImpact
            ? 'Apply Watchlist and accept removals'
            : isWatchlistUpload ? 'Apply' : 'Accept';
    const applyingLabel = unchangedWatchlistUpload
        ? 'Closing...'
        : isWatchlistUpload ? 'Applying...' : 'Importing...';
    const showCount = (key) => (
        !Array.isArray(visibleCountKeys) || visibleCountKeys.includes(key)
    );
    const source = pendingSourcePresentation(importRun, metadata);
    const runStatus = String(importRun.status || '').trim().toLowerCase();
    const holdingsAlreadyMatch = diffDetailsMode === 'portfolio'
        && runStatus === 'pending_apply'
        && !NON_RESETTING_SOURCES.has(importRun.source)
        && finiteNumber(counts.unchanged) > 0
        && ['added', 'removed', 'changed'].every((key) => finiteNumber(counts[key]) === 0);
    const status = msiResolutionPending
        ? {
            label: 'Processing',
            tooltip: 'This staged Watchlist upload is being processed',
            kind: 'info',
            color: 'info.main',
            loading: true,
        }
        : nothingMatched
            ? {
                label: 'Nothing added',
                tooltip: 'Nothing was added; your Watchlist is unchanged',
                kind: 'notice',
                color: 'info.main',
                loading: false,
            }
        : runStatus === 'failed'
            ? {
                label: 'Failed',
                tooltip: 'This import failed and was not applied',
                kind: 'error',
                color: 'error.main',
                loading: false,
            }
            : {
                label: 'Ready to review',
                tooltip: 'This import is staged and ready for review',
                kind: 'pending',
                color: 'info.main',
                loading: false,
            };

    return (
        <Paper
            variant="outlined"
            sx={[
                {
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                },
                watchlistPanelSx,
            ]}
        >
            <Box
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                mb={1.25}
            >
                <Box display="inline-flex" alignItems="center" gap={0.65} flexWrap="wrap" minWidth={0}>
                    <HistoryStatusIcon {...status} />
                    <HistoryCategoryChip {...source} />
                    <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>
                        {title || 'Pending Import'}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.67rem', whiteSpace: 'nowrap' }}
                    >
                        {importRun.created_at ? new Date(importRun.created_at).toLocaleString() : '—'}
                    </Typography>
                    {currentPortfolioTimestamp && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            Current portfolio: {new Date(currentPortfolioTimestamp).toLocaleString()}
                        </Typography>
                    )}
                </Box>
                <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    gap={1}
                    flexWrap="wrap"
                    sx={{ ml: 'auto' }}
                >
                    {!msiResolutionPending && (
                        <Box display="inline-flex" alignItems="center" gap={0.9}>
                            <HistorySummaryMetrics
                                added={showCount('added') ? finiteNumber(counts.added) : 0}
                                removed={showCount('removed') ? finiteNumber(counts.removed) : 0}
                                changed={showCount('changed') ? finiteNumber(counts.changed) : 0}
                                itemLabel={isWatchlistUpload || diffDetailsMode === 'watchlist_add'
                                    ? 'securities'
                                    : 'positions'}
                            />
                            {showCount('ignored') && (
                                <HistoryMetric
                                    label={`Ignored ${ignoredWatchlist.length || 0}`}
                                    value={ignoredWatchlist.length || 0}
                                    color="warning.main"
                                    symbol="⊘"
                                />
                            )}
                            {showCount('warnings') && (
                                <HistoryMetric
                                    label={`Warnings ${expansionWarnings.length || 0}`}
                                    value={expansionWarnings.length || 0}
                                    color="warning.main"
                                    symbol="!"
                                />
                            )}
                            {showCount('unchanged') && (
                                <HistoryMetric
                                    label={`${msiReviewPending ? 'Expected unchanged' : 'Unchanged'} ${counts.unchanged || 0}`}
                                    value={finiteNumber(counts.unchanged)}
                                    color="text.secondary"
                                    symbol="="
                                />
                            )}
                        </Box>
                    )}
                    {headerActions ? (
                        <Box display="flex" gap={1}>
                            {headerActions}
                        </Box>
                    ) : (
                        showActions && (
                            <Box display="flex" gap={1}>
                                {hasPortfolioImpact && onKeepInWatchlist && (
                                    <Button
                                        size="medium"
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => onKeepInWatchlist(importRun)}
                                        disabled={applying}
                                        sx={{ textTransform: 'none', fontWeight: 600, px: 1.5, minHeight: 32 }}
                                    >
                                        Keep in Watchlist
                                    </Button>
                                )}
                                <Tooltip title={
                                    effectiveApplyDisabled && effectiveApplyDisabledReason
                                        ? effectiveApplyDisabledReason
                                        : unchangedWatchlistUpload
                                            ? 'Close this unchanged upload'
                                            : (isWatchlistUpload ? 'Apply this staged upload' : 'Accept this import')
                                }>
                                    <span>
                                        <Button
                                            size="medium"
                                            variant="contained"
                                            color="success"
                                            onClick={() => onApply?.(importRun)}
                                            disabled={applying || effectiveApplyDisabled}
                                            startIcon={
                                                applying
                                                    ? <CircularProgress size={14} thickness={6} color="inherit" />
                                                    : <CheckCircleOutlineIcon fontSize="small" />
                                            }
                                            sx={{ textTransform: 'none', fontWeight: 600, px: 1.5, minHeight: 32 }}
                                        >
                                            {applying ? applyingLabel : applyLabel}
                                        </Button>
                                    </span>
                                </Tooltip>
                                {canIgnore && (
                                    <Tooltip title="Ignore this import">
                                        <span>
                                            <Button
                                                size="medium"
                                                variant="outlined"
                                                color="warning"
                                                onClick={() => onIgnore(importRun)}
                                                disabled={applying}
                                                startIcon={<NotInterestedIcon fontSize="small" />}
                                                sx={{ textTransform: 'none', fontWeight: 600, px: 1.5, minHeight: 32 }}
                                            >
                                                Ignore
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                        )
                    )}
                </Box>
            </Box>

            <ImportFailureNotice lifecycle={watchlistLifecycle} />
            {drasticWarning && !provisionalIdentityDiff && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {`This upload removes ${drasticRemovedCount} of ${drasticPriorCount} current Watchlist rows (${drasticRemovedPercent}). Apply will replace the active Watchlist and remove those securities from dependent portfolio state.`}
                </Alert>
            )}
            {watchlistStale && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                    This import was reviewed against an older Watchlist. Ignore it and stage a
                    new import before applying.
                </Alert>
            )}
            {holdingsAlreadyMatch && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                    These holdings already match your Actual Portfolio, so it won&apos;t change.
                    Accepting resets Position Sizing to these holdings and replaces any edits
                    made there.
                </Alert>
            )}
            {duplicateOfCurrent && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                    This upload already matches the current active Watchlist. No processing or table reload is needed.
                </Alert>
            )}
            {nothingMatched && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                    {nothingMatchedMessage({
                        progress: watchlistLifecycle.progress,
                        inputValidation,
                    })}
                </Alert>
            )}
            {cleanupSummary && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                    {cleanupSummary}
                </Alert>
            )}

            {hasPortfolioImpact && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        This Watchlist change affects portfolios
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Keep these securities in the staged Watchlist, or apply the Watchlist
                        and accept their removal from every affected portfolio.
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Table size="small" stickyHeader aria-label="Portfolios affected by Watchlist removals">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ minWidth: 220 }}>
                                        <SecurityIdentityColumnHeader
                                            value={impactIdentityField}
                                            selectorId={WATCHLIST_IMPACT_IDENTITY_SELECTOR_ID}
                                            anchorEl={impactIdentityAnchorEl}
                                            onOpen={setImpactIdentityAnchorEl}
                                        />
                                    </TableCell>
                                    <TableCell>Affected portfolios</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {portfolioImpactRows.map((row) => {
                                    const identity = row?.display_identity || {};
                                    const submittedIdentifier = securitySubmittedIdentifier({
                                        inbound_id_type: identity.submitted_identifier_type,
                                        inbound_id_value: identity.submitted_identifier,
                                    }, { includeTickerType: true });
                                    const security = impactIdentityField === 'ticker'
                                        ? identity.ticker
                                        : impactIdentityField === 'submitted_identifier'
                                            ? submittedIdentifier
                                            : identity.company_name || identity.ticker || submittedIdentifier;
                                    return (
                                        <TableRow key={row.cognive_id}>
                                            <TableCell>{security || '—'}</TableCell>
                                            <TableCell>
                                                {(row.affected_portfolios || []).join(', ') || '—'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <SecurityIdentityFieldSelector
                        anchorEl={impactIdentityAnchorEl}
                        onClose={() => setImpactIdentityAnchorEl(null)}
                        selectorId={WATCHLIST_IMPACT_IDENTITY_SELECTOR_ID}
                        value={impactIdentityField}
                        onChange={setImpactIdentityField}
                        ariaLabel="Identity field displayed for affected portfolio securities"
                    />
                </Box>
            )}

            {msiResolutionPending ? (
                <Alert severity="info">
                    You accepted this upload and MSI v2 is now resolving it. Your active Watchlist
                    remains unchanged until resolution succeeds and the accepted revision can be
                    applied safely.
                </Alert>
            ) : (!summaryOnly || ignoredWatchlist.length > 0 || expansionWarnings.length > 0) && (
                <ImportDiffDetails
                    diffDetails={diffDetails}
                    diffSummary={diffSummary}
                    ignoredWatchlist={ignoredWatchlist}
                    visibleSections={visibleDiffSections}
                    mode={diffDetailsMode}
                    notConsidered={notConsidered}
                    emptyMessage={metadata.action === 'watchlist_context_refresh'
                        ? 'No security-list changes are proposed. All saved securities will be rechecked under the available context.'
                        : holdingsAlreadyMatch ? 'No changes to your holdings.' : undefined}
                />
            )}
        </Paper>
    );
}

PendingImportCard.propTypes = {
    importRun: PropTypes.object,
    onApply: PropTypes.func,
    onIgnore: PropTypes.func,
    applying: PropTypes.bool,
    showActions: PropTypes.bool,
    title: PropTypes.string,
    currentPortfolioTimestamp: PropTypes.string,
    headerActions: PropTypes.node,
    summaryOnly: PropTypes.bool,
    visibleCountKeys: PropTypes.arrayOf(PropTypes.string),
    visibleDiffSections: PropTypes.arrayOf(PropTypes.string),
    diffDetailsMode: PropTypes.oneOf(['portfolio', 'watchlist_add', 'watchlist_upload']),
    applyDisabled: PropTypes.bool,
    applyDisabledReason: PropTypes.string,
    onKeepInWatchlist: PropTypes.func,
};
